import { createBrowserRouter } from "react-router-dom";
import Home from "./pages/Home";
import About from "./pages/About";
import LoginPage from "./pages/LoginPage";
import RegistrationPage from "./pages/RegistrationPage";
import LandingPage from "./pages/LandingPage";
import Checkout from "./pages/Checkout";
import Logout from "./pages/Logout";
import NotFoundPage from "./pages/NotFoundPage";
import ProfilePage from "./pages/ProfilePage";

import Article from "./pages/Article";
import ArticleItem from "./pages/ArticleItem";
import ArticleItemContainer from "./pages/ArticleItemContainer";
import UserFlowDiagramContainer from "./pages/UserFlowDiagramContainer";

import Dashboard from "./pages/Dashboard";

// Import role-specific landing pages
import { AdminLandingPage, BookCreatorLandingPage, VoiceActorLandingPage } from "./pages/landing";
import AddBooksLandingPage from "./pages/landing/AddBooksLandingPage";
import AddBookLandingPage from "./pages/landing/AddBookLandingPage";
import AddLiteraryGroupLandingPage from "./pages/landing/AddLiteraryGroupLandingPage";
import ManageOpportunitiesLandingPage from "./pages/landing/ManageOpportunitiesLandingPage";
import TextLibraryLandingPage from "./pages/landing/TextLibraryLandingPage";
import PublishPrototypesPage from "./pages/landing/PublishPrototypesPage";
import DebugUserRole from "./pages/landing/DebugUserRole";
import RoleBasedRedirect from "./components/RoleBasedRedirect";
import Layout from "./components/Layout";
import ProtectedRoute from "./ProtectedRoute";

// Import the integrated Voice Lab components
import VoiceLabCharacterList from "./pages/subPages/VoiceLabCharacterList.integrated";
import VoiceLabMyRecordings from "./pages/subPages/VoiceLabMyRecordings.integrated";
import VoiceLabIntegrationDemo from "./pages/subPages/VoiceLabIntegrationDemo";
import VoiceLabNestedTest from "./pages/subPages/VoiceLabNestedTest";

// In your router file (likely src/router.jsx or similar)
// ARCHIVED: import MySampleVoices from './pages/subPages/MySampleVoices';
// Import Voice Sample Management
import VoiceSampleWorksheet from "./pages/subPages/voiceActor/VoiceSampleWorksheet";
// ARCHIVED: import WorkInProgress from './pages/subPages/WorkInProgress';
import MessagingTest from './pages/subPages/MessagingTest';
import TalentAgencyPage from './pages/subPages/TalentAgencyPage';

// Import Book Creator Worksheet Wrappers
// import BookCreatorWorksheetWrapper from "./components/BookCreatorWorksheetWrapper";
// import NewBookCreatorWorksheetWrapper from "./components/NewBookCreatorWorksheetWrapper";
// import NewBookCreatorWorksheetNormalizedWrapper from "./components/NewBookCreatorWorksheetNormalizedWrapper";

// Import Voice Cloning Pipeline (Book Creator)
import VoiceCloningPipeline from "./pages/subPages/bookCreator/VoiceCloningPipeline";
import RenderingStudio from "./pages/subPages/bookCreator/RenderingStudio";
import ReleasesManager from "./pages/subPages/bookCreator/ReleasesManager";


// Import Voice Opportunities components
import OpportunityMarketplace from "./pages/subPages/voiceActor/OpportunityMarketplace";
import OpportunityDetails from "./pages/subPages/voiceActor/OpportunityDetails";
import ApplicationSubmission from "./pages/subPages/voiceActor/ApplicationSubmission";
import CreateOpportunity from "./pages/subPages/voiceActor/CreateOpportunity";
import ManageOpportunities from "./pages/subPages/voiceActor/ManageOpportunities";
import ApplicationsList from "./pages/subPages/voiceActor/ApplicationsList";
import ApplicationDetails from "./pages/subPages/voiceActor/ApplicationDetails";
import RecordingStudio from "./pages/subPages/voiceActor/RecordingStudio";

// Import Path Registry Management components
import PathRegistryManager from "./pages/admin/PathRegistryManager";
import EmotionCategoryMatrixManager from "./pages/admin/EmotionCategoryMatrixManager";
import ClientReleasesManager from "./pages/admin/ClientReleasesManager";
import ReaderNotes from "./pages/admin/ReaderNotes";
import ReaderAccess from "./pages/admin/ReaderAccess";
import ListenPage from "./pages/listen/ListenPage";
import PathRegistryExample from "./components/examples/PathRegistryExample";

// Import Security Registry Management components
import SecurityRegistryManager from "./pages/admin/SecurityRegistryManager";

// Import User Management components
import UserManagement from "./pages/admin/UserManagement";
import DeleteUser from "./pages/admin/DeleteUser";
import AddUser from "./pages/admin/AddUser";
import AdminUserProfileEditor from "./pages/admin/AdminUserProfileEditor";

// Import SignedUrlTest component
import SignedUrlTest from "./components/SignedUrlTest";

// Import Enhanced Messaging Test component
import EnhancedMessagingTest from "./pages/subPages/EnhancedMessagingTest";

// Import Book Worksheet components
import { BookWorksheetPageWrapperEnh } from "./bookWorksheet";

// Import VoiceActor Worksheet components
import { VoiceActorWorksheetPageWrapperEnh } from "./voiceActorWorksheet";

// Import Character-Context Hover-Panel Mockup (DEV-ONLY).
// See docs/journal/PHASE_4D_VA_HOVER_PANEL_VISUAL_DESIGN_MOCKUP.md.
// The route below is conditionally added only when NODE_ENV !== 'production'.
import HoverPanelDemo from "./components/characterContext/HoverPanelMockup/HoverPanelDemo";
// Trigger + popover demo — used to verify the inline ⓥ + viewport-docked
// popover behaviour outside the marketplace before reviewing live host pages.
import CharacterContextTriggerDemo from "./components/characterContext/CharacterContextTriggerDemo";

// Import VA Fidelity Gate inline-panel mockup (DEV-ONLY).
// See docs/journal/PHASE_4D_VA_FIDELITY_GATE_DESIGN.md.
// The route below is conditionally added only when NODE_ENV !== 'production'.
import FidelityPanelDemo from "./components/voiceFidelity/FidelityPanelMockup/FidelityPanelDemo";

// Build the dev-only routes set so the production bundle never sees them.
const devOnlyRoutes =
  process.env.NODE_ENV !== 'production'
    ? [
      {
        // Dev-only — character-context hover-panel mockup. No nav link.
        // Navigate manually to review/tune visual design. See README.md
        // in src/components/characterContext/HoverPanelMockup/.
        path: "/dev/hover-panel-mockup",
        element: <HoverPanelDemo />,
      },
      {
        // Dev-only — inline ⓥ trigger + viewport-docked popover preview.
        // Lets us tune trigger affordance and slide-in behaviour in
        // isolation, without the noise of the live opportunity marketplace.
        path: "/dev/character-context-trigger",
        element: <CharacterContextTriggerDemo />,
      },
      {
        // Dev-only — VA Fidelity Gate inline-panel mockup. No nav link.
        // Lets us tune GREEN/AMBER/RED/GRAY/PENDING band copy + palette
        // before wiring the real Whisper-driven scorer into RecordingStudio.
        // See README.md in src/components/voiceFidelity/FidelityPanelMockup/.
        path: "/dev/fidelity-panel-mockup",
        element: <FidelityPanelDemo />,
      },
    ]
    : [];




export const router = createBrowserRouter([

  // /listen — the client download page. A TOP-LEVEL sibling of "/", not a child: no <Layout />, no
  // navbar, no app login. Its own key → listener token. See docs/journal/CLIENT_LISTEN_PAGE_DESIGN.md.
  { path: "/listen", element: <ListenPage /> },

  /**
   * ⭐ /sample/:id — the "hear a sample" page, and a TOP-LEVEL SIBLING for the same reason /listen
   * is. The operator: *"the ‘Hear a sample’ page should just be the content with a header and no
   * navigation for the site (and no Please login, or register prompt)."*
   *
   * ⓘ `<Layout />` is where all of that comes from — Navbar, the floating message icon, and the
   * sign-in prompts the Navbar carries. A route OUTSIDE its children gets none of it, which is
   * cheaper and more honest than teaching ArticleItem to hide things.
   *
   * ⛔ THE OLD PATH STAYS. `/articleItemPage/:id` is in welcome and invitation emails that have
   * ALREADY BEEN SENT, and it is also how the Info archive links its own articles — which DO want
   * the navbar, because a reader there needs a way back. Same component, two front doors.
   */
  { path: "/sample/:id", element: <ArticleItem /> },

  /**
   * ⭐ /guide/:id — the same bare-article door, for the "How to listen" instructions that /listen
   * links from its help popover. A sibling of "/", so no navbar and no sign-in prompt for a reader
   * who may have no account at all.
   *
   * ⓘ THE ARTICLE IS ALSO IN THE INFO SET, deliberately: it is an ordinary :ArticleItem in the
   * `Articles` container with the `tutorial` category, so `/articleItemPage/:id` shows it WITH the
   * navbar for somebody browsing Info, and `/guide/:id` shows it bare for somebody sent there. Two
   * front doors, one article, one set of words to maintain.
   */
  { path: "/guide/:id", element: <ArticleItem /> },

  {
    path: "/",
    element: <Layout />,
    children: [
      // Dev-only routes (empty array in production). Placed first so they
      // resolve before any catch-all. See devOnlyRoutes const above.
      ...devOnlyRoutes,

      // Debug route to see user role state
      {
        path: "/debug-user-role",

        element: <ProtectedRoute><DebugUserRole /></ProtectedRoute>
      },
      // Manage Opportunities Landing Page (restored with user state fix)
      {
        path: "/manage-opportunities-landing",
        element: <ProtectedRoute allowedRoles={['bookCreator', 'admin']}><ManageOpportunitiesLandingPage /></ProtectedRoute>
      },
      // Public routes
      { path: "/", element: <Home /> },
      { path: "/home", element: <Home /> },
      { path: "/about", element: <About /> },
      { path: "/login", element: <LoginPage /> },
      { path: "/register", element: <RegistrationPage /> },
      { path: "/landing", element: <LandingPage /> }, // Guest landing page

      // Legacy landing page (will be replaced by role-specific landing pages)
      {
        path: "/landingPage",
        element: <ProtectedRoute><RoleBasedRedirect /></ProtectedRoute>
      },

      // Role-specific landing pages
      {
        path: "/landing/admin",
        element: <ProtectedRoute allowedRoles={['admin']}><AdminLandingPage /></ProtectedRoute>
      },
      {
        path: "/landing/book-creator",
        element: <ProtectedRoute allowedRoles={['bookCreator']}><BookCreatorLandingPage /></ProtectedRoute>
      },
      {
        path: "/landing/voice-actor",
        element: <ProtectedRoute allowedRoles={['voiceActor']}><VoiceActorLandingPage /></ProtectedRoute>
      },

      // Add Books Landing Page (Hub)
      {
        path: "/add-books-landing",
        element: <ProtectedRoute allowedRoles={['bookCreator', 'admin']}><AddBooksLandingPage /></ProtectedRoute>
      },

      // Add Book Landing Page (Book Management)
      {
        path: "/add-book-landing",
        element: <ProtectedRoute allowedRoles={['bookCreator', 'admin']}><AddBookLandingPage /></ProtectedRoute>
      },

      // Add Literary Group Landing Page
      {
        path: "/add-literary-group-landing",
        element: <ProtectedRoute allowedRoles={['bookCreator', 'admin']}><AddLiteraryGroupLandingPage /></ProtectedRoute>
      },

      // Add Litgroups/Books
      {
        path: "/add-litgroups-books", // was path: "/landing/text-library",
        element: <ProtectedRoute allowedRoles={['bookCreator', 'admin']}><TextLibraryLandingPage /></ProtectedRoute>
      },

      // Publish Prototypes Page
      {
        path: "/publish-prototypes",
        element: <ProtectedRoute allowedRoles={['bookCreator', 'admin']}><PublishPrototypesPage /></ProtectedRoute>
      },

      // Voice Cloning Pipeline (Book Creator) — track approved speech
      // samples as they are exported to and trained by ElevenLabs.
      {
        path: "/voice-cloning/pipeline",
        element: <ProtectedRoute allowedRoles={['bookCreator', 'admin']}><VoiceCloningPipeline /></ProtectedRoute>
      },

      // Phase 4d Step 3 — Rendering Studio R1. Eye-test the per-character
      // 9-cell speech-sample grid against a freshly-cloned voice before
      // committing to scene rendering. Same audience (BC + admin) as the
      // cloning pipeline. Path is nested under /dashboard/book-creator/* per
      // the handoff spec (PHASE_4D_RENDERING_STUDIO_R1_SERVER_COMPLETE §3) so
      // it sits beside future BC-scoped tools without polluting the flat
      // top-level path namespace.
      {
        path: "/dashboard/book-creator/rendering-studio",
        element: <ProtectedRoute allowedRoles={['bookCreator', 'admin']}><RenderingStudio /></ProtectedRoute>
      },

      // AAI Talent Agency — Casting Studio. Same BC + admin audience; nested under
      // /dashboard/book-creator/* beside the other BC-scoped tools.
      {
        path: "/dashboard/book-creator/talent-agency",
        element: <ProtectedRoute allowedRoles={['bookCreator', 'admin']}><TalentAgencyPage /></ProtectedRoute>
      },

      // Releases Manager — the creator's read-only view of their recipients. BC + admin, nested
      // beside the other BC-scoped tools. The server scopes by traversal from the caller's own
      // user node, so an admin landing here sees THEIR recipients, which may legitimately be none.
      {
        path: "/dashboard/book-creator/releases",
        element: <ProtectedRoute allowedRoles={['bookCreator', 'admin']}><ReleasesManager /></ProtectedRoute>
      },



      // Protected routes
      {
        path: "/dashBoard",
        element: <ProtectedRoute><Dashboard /></ProtectedRoute>
      },
      {
        path: "/checkout",
        element: <ProtectedRoute><Checkout /></ProtectedRoute>
      },
      // Article / "Info" routes — intentionally PUBLIC.
      // The Navbar shows the Info link to all visitors (logged-in or not),
      // and the article content itself is informational/marketing material
      // (no PII, no privileged data). Wrapping these in <ProtectedRoute>
      // caused the Info link to redirect logged-out visitors to /login,
      // which made the link feel broken. If a specific article ever needs
      // gating, do it inside the page component (per-article check), not
      // by re-wrapping the whole route family.
      {
        path: "/article",
        element: <Article />
      },
      {
        path: "/articleItemContainer/:id",
        element: <ArticleItemContainer />
      },
      {
        path: "/articleItemPage/:id",
        element: <ArticleItem />
      },
      {
        path: "/userFlowDiagram",
        element: <ProtectedRoute><UserFlowDiagramContainer /></ProtectedRoute>
      },
      {
        path: "/logout",
        element: <ProtectedRoute><Logout /></ProtectedRoute>
      },
      {
        path: "/profile",
        element: <ProtectedRoute><ProfilePage /></ProtectedRoute>
      },

      // Voice Lab integrated routes
      {
        path: "/voice-lab/characters",
        element: <ProtectedRoute><VoiceLabCharacterList /></ProtectedRoute>
      },
      {
        path: "/voice-lab/recordings",
        element: <ProtectedRoute><VoiceLabMyRecordings /></ProtectedRoute>
      },
      {
        path: "/voice-lab/demo",
        element: <ProtectedRoute><VoiceLabIntegrationDemo /></ProtectedRoute>
      },
      {
        path: "/voice-lab/nested-test",
        element: <ProtectedRoute><VoiceLabNestedTest /></ProtectedRoute>
      },
      {
        path: "/voice-lab/signed-url-test",
        element: <ProtectedRoute><SignedUrlTest /></ProtectedRoute>
      },

      // Voice Opportunities routes
      {
        path: "/opportunities",
        element: <ProtectedRoute><OpportunityMarketplace /></ProtectedRoute>
      },
      {
        path: "/opportunities/create",
        element: <ProtectedRoute allowedRoles={['bookCreator', 'admin']}><CreateOpportunity /></ProtectedRoute>
        // element: <ProtectedRoute><CreateOpportunity /></ProtectedRoute>

      },
      {
        path: "/opportunities/manage",
        element: <ProtectedRoute allowedRoles={['bookCreator', 'admin']}><ManageOpportunities /></ProtectedRoute>
        // element: <ProtectedRoute ><ManageOpportunities /></ProtectedRoute>

      },
      {
        path: "/opportunities/:id",
        element: <ProtectedRoute><OpportunityDetails /></ProtectedRoute>
      },
      {
        path: "/opportunities/:id/apply",
        element: <ProtectedRoute allowedRoles={['voiceActor', 'admin']}><ApplicationSubmission /></ProtectedRoute>
      },
      {
        path: "/opportunities/:id/applications",
        element: <ProtectedRoute allowedRoles={['bookCreator', 'admin']}><ApplicationsList /></ProtectedRoute>
      },
      {
        path: "/applications/:id",
        element: <ProtectedRoute><ApplicationDetails /></ProtectedRoute>
      },
      {
        path: "/recording-studio/:applicationId",
        element: <ProtectedRoute allowedRoles={['voiceActor', 'admin']}><RecordingStudio /></ProtectedRoute>
      },
      // ARCHIVED: MySampleVoices page moved to archived-legacy-voice-lab
      // {
      //   path: "/my-sample-voices",
      //   element: <ProtectedRoute><MySampleVoices /></ProtectedRoute>
      // },
      // ARCHIVED: Work in Progress page removed
      // {
      //   path: "/work-in-progress",
      //   element: <ProtectedRoute><WorkInProgress /></ProtectedRoute>
      // },
      {
        path: "/messaging-test",
        element: <ProtectedRoute allowedRoles={['admin']}><MessagingTest /></ProtectedRoute>
      },
      {
        path: "/enhanced-messaging-test",
        element: <ProtectedRoute allowedRoles={['admin']}><EnhancedMessagingTest /></ProtectedRoute>
      },


      // Book Worksheet route
      {
        path: "/book-worksheet-enh",
        element: (
          <ProtectedRoute>
            <BookWorksheetPageWrapperEnh />
          </ProtectedRoute>
        )
      },

      // VoiceActor Worksheet route
      {
        path: "/voice-actor-worksheet-enh",
        element: (
          <ProtectedRoute allowedRoles={['voiceActor']}>
            <VoiceActorWorksheetPageWrapperEnh />
          </ProtectedRoute>
        )
      },

      // Voice Sample Management route
      //        path: "/voice-lab/sample-manager",

      {
        path: "/voice-lab/sample-manager",
        element: (<ProtectedRoute allowedRoles={['voiceActor', 'admin']}> <VoiceSampleWorksheet /> </ProtectedRoute>
        )
      },

      // Path Registry Management routes
      {
        path: "/admin/path-registry/*",
        element: <ProtectedRoute allowedRoles={['admin']}><PathRegistryManager /></ProtectedRoute>
      },
      {
        path: "/admin/path-registry-example",
        element: <ProtectedRoute allowedRoles={['admin']}><PathRegistryExample /></ProtectedRoute>
      },

      // Emotion → Category translation matrix (admin-editable registry)
      {
        path: "/admin/emotion-category-matrix",
        element: <ProtectedRoute allowedRoles={['admin']}><EmotionCategoryMatrixManager /></ProtectedRoute>
      },

      // Client downloads — the admin side of /listen (releases and access keys)
      {
        path: "/admin/client-downloads",
        element: <ProtectedRoute allowedRoles={['admin']}><ClientReleasesManager /></ProtectedRoute>
      },

      // Reader notes — what the quality-control readers found (Book | where | what).
      //
      // ⓘ ADMIN-ONLY HERE, THOUGH THE API IS NOT. /api/reader-notes is guarded by `auth`, and the
      // service scopes an ordinary creator to notes on books reached from their own :LitGroups --
      // an admin gets the whole table because the production books live under bookcreator-1 while
      // quirkmaker is the only admin. This route is where the operator reads them; a creator-facing
      // surface can be added without touching the server.
      {
        path: "/admin/reader-notes",
        element: <ProtectedRoute allowedRoles={['admin']}><ReaderNotes /></ProtectedRoute>
      },

      // Reader access — tick what a reader may hear, and invite them by email.
      //
      // ⓘ ADMIN-ONLY HERE, THOUGH THE ENDPOINTS ARE NOT. /api/reader-access and /api/invitations
      // are guarded by `auth` and scoped by traversal in the service, so when this becomes a Book
      // Creator feature only this guard changes — the screen does not.
      {
        path: "/admin/reader-access",
        element: <ProtectedRoute allowedRoles={['admin']}><ReaderAccess /></ProtectedRoute>
      },

      // Security Registry Management routes
      {
        path: "/admin/security-registry/*",
        element: <ProtectedRoute allowedRoles={['admin']}><SecurityRegistryManager /></ProtectedRoute>
      },

      // User Management routes
      {
        path: "/admin/user-management",
        element: <ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>
      },
      {
        path: "/admin/add-user",
        element: <ProtectedRoute allowedRoles={['admin']}><AddUser /></ProtectedRoute>
      },
      {
        path: "/admin/delete-user",
        element: <ProtectedRoute allowedRoles={['admin']}><DeleteUser /></ProtectedRoute>
      },
      {
        path: "/admin/user-profile/:userId",
        element: <ProtectedRoute allowedRoles={['admin']}><AdminUserProfileEditor /></ProtectedRoute>
      },

      // 404 route
      { path: "*", element: <NotFoundPage /> }
    ]
  }
]);
